import type {
  FunnelMomentStageCount,
  FunnelStageCounts,
} from "@repo/app-types/api/funnel";
import type {
  FunnelMoments,
  FunnelStages,
} from "@repo/app-types/entities/funnel";

const shareFormat = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumFractionDigits: 0,
});

/** A share in 0..1 as a whole percentage, or a dash. */
export function formatFunnelShare(share: number | null): string {
  return share === null ? "–" : shareFormat.format(share);
}

const preciseShareFormat = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumFractionDigits: 1,
});

/** A share in 0..1 as a percentage to one decimal. */
export function formatFunnelPreciseShare(share: number): string {
  return preciseShareFormat.format(share);
}

/** The name of the stage an exchange happened in; null is before any stage. */
export function formatFunnelStageName(
  stages: FunnelStages,
  stageKey: string | null,
): string {
  if (stageKey === null) {
    const firstStage = stages[0];
    return firstStage ? `Before ${firstStage.name}` : "Before any stage";
  }
  return stages.find((stage) => stage.key === stageKey)?.name ?? stageKey;
}

/** A stage's interactions in prose: "Offer interactions", "interactions before Greeting". */
export function formatFunnelStageInteractions(
  stages: FunnelStages,
  stageKey: string | null,
): string {
  if (stageKey === null) {
    const firstStage = stages[0];
    return `interactions before ${firstStage ? firstStage.name : "any stage"}`;
  }
  return `${formatFunnelStageName(stages, stageKey)} interactions`;
}

/** Interactions that reached a stage; before the first stage, all of them. */
function findFunnelStageInteractions(
  stageCounts: FunnelStageCounts,
  stageKey: string | null,
): number {
  if (stageKey === null) {
    return stageCounts.denominator;
  }
  return (
    stageCounts.stages.find((stage) => stage.stage_key === stageKey)?.reached ??
    0
  );
}

export const FunnelFlowNodeKind = {
  Start: "start",
  Stage: "stage",
  Exit: "exit",
} as const;

export type FunnelFlowNodeKind =
  (typeof FunnelFlowNodeKind)[keyof typeof FunnelFlowNodeKind];

export interface FunnelFlowNode {
  kind: FunnelFlowNodeKind;
  name: string;
  /** Interactions. */
  value: number;
  /** The stage selecting this node shows; null is before the first stage. */
  stageKey: string | null;
}

export interface FunnelFlowLink {
  source: number;
  target: number;
  /** Interactions. */
  value: number;
  isExit: boolean;
  /** The source node's stage. */
  stageKey: string | null;
}

export interface FunnelFlowData {
  nodes: FunnelFlowNode[];
  links: FunnelFlowLink[];
}

/**
 * The Sankey of one cohort's interactions: a start node, then each stage, each
 * but the last with an exit for the interactions that went no further. Links
 * worth nothing are left out, and so are the nodes that leaves unconnected.
 */
export function buildFunnelFlowData(
  stages: FunnelStages,
  stageCounts: FunnelStageCounts,
): FunnelFlowData {
  const countByKey = new Map(
    stageCounts.stages.map((stageCount) => [stageCount.stage_key, stageCount]),
  );
  const stageNodes = stages.map((stage): FunnelFlowNode => ({
    kind: FunnelFlowNodeKind.Stage,
    name: stage.name,
    value: countByKey.get(stage.key)?.reached ?? 0,
    stageKey: stage.key,
  }));
  const startNode: FunnelFlowNode = {
    kind: FunnelFlowNodeKind.Start,
    name: "All interactions",
    value: stageCounts.denominator,
    stageKey: null,
  };
  // The start node, then one per stage.
  const flowNodes = [startNode, ...stageNodes];

  const nodes: FunnelFlowNode[] = [...flowNodes];
  const links: FunnelFlowLink[] = [];
  flowNodes.forEach((node, index) => {
    const nextNode = flowNodes[index + 1];
    if (!nextNode) {
      return;
    }
    links.push({
      source: index,
      target: index + 1,
      value: nextNode.value,
      isExit: false,
      stageKey: node.stageKey,
    });
    const exited =
      node.stageKey === null
        ? stageCounts.reached_no_stage
        : (countByKey.get(node.stageKey)?.exited ?? 0);
    nodes.push({
      kind: FunnelFlowNodeKind.Exit,
      name: `Exited ${node.stageKey === null ? "before any stage" : `after ${node.name}`}`,
      value: exited,
      stageKey: node.stageKey,
    });
    links.push({
      source: index,
      target: nodes.length - 1,
      value: exited,
      isExit: true,
      stageKey: node.stageKey,
    });
  });

  const nonZeroLinks = links.filter((link) => link.value > 0);
  const connected = new Set(
    nonZeroLinks.flatMap((link) => [link.source, link.target]),
  );
  const newIndexByOldIndex = new Map<number, number>();
  const connectedNodes = nodes.filter((_node, index) => {
    if (!connected.has(index)) {
      return false;
    }
    newIndexByOldIndex.set(index, newIndexByOldIndex.size);
    return true;
  });
  return {
    nodes: connectedNodes,
    links: nonZeroLinks.map((link) => ({
      ...link,
      source: newIndexByOldIndex.get(link.source) ?? link.source,
      target: newIndexByOldIndex.get(link.target) ?? link.target,
    })),
  };
}

/** One moment in the selected stage, for the agent cohort. */
export interface FunnelStageMomentRow {
  momentKey: string;
  momentName: string;
  momentDescription: string;
  interactions: number;
  continued: number;
  droppedOff: number;
  /** Interactions that reached the stage. */
  stageInteractions: number;
  /** `interactions` over `stageInteractions`. */
  share: number;
  /** `droppedOff` over `interactions`; null in the last stage, where every interaction ends. */
  dropOffRate: number | null;
}

/** The moments that came up in a stage, the most dropped off first. */
export function buildFunnelStageMomentRows(input: {
  stages: FunnelStages;
  moments: FunnelMoments;
  momentCounts: readonly FunnelMomentStageCount[];
  stageCounts: FunnelStageCounts;
  /** Null is before the first stage. */
  stageKey: string | null;
}): FunnelStageMomentRow[] {
  const { stages, moments, momentCounts, stageCounts, stageKey } = input;
  const stageInteractions = findFunnelStageInteractions(stageCounts, stageKey);
  const isLastStage = stageKey !== null && stages.at(-1)?.key === stageKey;
  const countByMomentKey = new Map(
    momentCounts
      .filter((count) => count.stage_key === stageKey)
      .map((count) => [count.moment_key, count]),
  );

  return moments
    .flatMap((moment): FunnelStageMomentRow[] => {
      const count = countByMomentKey.get(moment.key);
      if (!count || count.interactions === 0 || stageInteractions === 0) {
        return [];
      }
      return [
        {
          momentKey: moment.key,
          momentName: moment.name,
          momentDescription: moment.description,
          interactions: count.interactions,
          continued: count.continued,
          droppedOff: count.dropped_off,
          stageInteractions,
          share: count.interactions / stageInteractions,
          dropOffRate: isLastStage
            ? null
            : count.dropped_off / count.interactions,
        },
      ];
    })
    .sort(
      (a, b) => b.droppedOff - a.droppedOff || b.interactions - a.interactions,
    );
}

/** A cohort's share of a stage's interactions with a moment; null without any. */
export function computeFunnelMomentShare(input: {
  momentCounts: readonly FunnelMomentStageCount[];
  stageCounts: FunnelStageCounts;
  momentKey: string;
  stageKey: string | null;
}): number | null {
  const { momentCounts, stageCounts, momentKey, stageKey } = input;
  const count = momentCounts.find(
    (candidate) =>
      candidate.moment_key === momentKey && candidate.stage_key === stageKey,
  );
  const stageInteractions = findFunnelStageInteractions(stageCounts, stageKey);
  if (!count || count.interactions === 0 || stageInteractions === 0) {
    return null;
  }
  return count.interactions / stageInteractions;
}
